import {
  BridgeDeviceStatus,
  IBridgeDevice,
  IPrincipleBridgeIntegrationSettings,
} from '@principle-theorem/principle-core/interfaces';
import { WithRef } from '@principle-theorem/shared';

type IntegrationKey = keyof IPrincipleBridgeIntegrationSettings;

export interface IBridgeDeviceIntegrationSummary {
  deviceName: string;
  deviceId: string;
  status: BridgeDeviceStatus;
  deleted: boolean;
  enabledIntegrations: string[];
}

export interface IIntegrationPathConflict {
  normalisedPath: string;
  usages: IIntegrationPathUsage[];
}

export interface IIntegrationPathUsage {
  deviceName: string;
  deviceId: string;
  integrationName: string;
  fieldName: string;
  path: string;
}

export interface IBridgeConfigConflictReport {
  integrationSummaries: IBridgeDeviceIntegrationSummary[];
  conflicts: IIntegrationPathConflict[];
}

/**
 * Only fields that MUST be unique per bridge device. Shared infrastructure
 * paths (e.g. appPath, pdataPath, serverUrl) are intentionally excluded —
 * multiple bridges legitimately point at the same executable or database.
 */
const UNIQUE_PATH_FIELDS: Partial<
  Record<IntegrationKey, { fields: string[]; label: string }>
> = {
  sidexis: {
    fields: ['principleMailboxPath', 'stationName'],
    label: 'Sidexis',
  },
  twain: {
    fields: ['imagePath'],
    label: 'TWAIN',
  },
  vixWin: {
    fields: ['imagePath'],
    label: 'VixWin',
  },
  vistaSoft: {
    fields: ['importFilePath', 'bridgePath'],
    label: 'VistaSoft',
  },
  dtxStudio: {
    fields: ['imageDirectory'],
    label: 'DTX Studio',
  },
  dtxStudioDriverDirect: {
    fields: ['imageDirectory'],
    label: 'DTX Studio Driver Direct',
  },
  dentalEye: {
    fields: ['imagePath'],
    label: 'DentalEye',
  },
};

export function checkBridgeConfigConflicts(
  devices: WithRef<IBridgeDevice>[]
): IBridgeConfigConflictReport {
  const activeDevices = devices.filter(
    (device) => !device.deleted && device.deviceId
  );
  const integrationSummaries = buildIntegrationSummaries(devices);
  const pathEntries = collectUniquePathEntries(activeDevices);
  const conflicts = findConflicts(pathEntries);

  return { integrationSummaries, conflicts };
}

function buildIntegrationSummaries(
  devices: WithRef<IBridgeDevice>[]
): IBridgeDeviceIntegrationSummary[] {
  return devices.map((device) => ({
    deviceName: device.name,
    deviceId: device.deviceId ?? '',
    status: device.status ?? BridgeDeviceStatus.Inactive,
    deleted: device.deleted,
    enabledIntegrations: getEnabledIntegrations(device.settings),
  }));
}

function getEnabledIntegrations(
  settings?: Partial<IPrincipleBridgeIntegrationSettings>
): string[] {
  if (!settings) {
    return [];
  }

  const enabled: string[] = [];
  for (const key of Object.keys(settings) as IntegrationKey[]) {
    const integration = settings[key];
    if (integration?.enabled) {
      enabled.push(key);
    }
  }
  return enabled;
}

function collectUniquePathEntries(
  devices: WithRef<IBridgeDevice>[]
): IIntegrationPathUsage[] {
  const entries: IIntegrationPathUsage[] = [];

  for (const device of devices) {
    const settings = device.settings;
    if (!settings || !device.deviceId) {
      continue;
    }

    for (const [key, config] of Object.entries(UNIQUE_PATH_FIELDS)) {
      const integration = settings[key as IntegrationKey];
      if (!integration?.enabled || !config) {
        continue;
      }

      for (const field of config.fields) {
        const value = (integration as unknown as Record<string, unknown>)[
          field
        ];
        if (typeof value === 'string' && value.trim().length > 0) {
          entries.push({
            deviceName: device.name,
            deviceId: device.deviceId,
            integrationName: config.label,
            fieldName: field,
            path: value.trim(),
          });
        }
      }
    }
  }

  return entries;
}

function findConflicts(
  entries: IIntegrationPathUsage[]
): IIntegrationPathConflict[] {
  const pathMap = new Map<string, IIntegrationPathUsage[]>();

  for (const entry of entries) {
    const normalised = normalisePath(entry.path);
    const existing = pathMap.get(normalised) ?? [];
    existing.push(entry);
    pathMap.set(normalised, existing);
  }

  return [...pathMap.entries()]
    .filter(([_, usages]) => usages.length > 1)
    .map(([normalisedPath, usages]) => ({ normalisedPath, usages }));
}

function normalisePath(path: string): string {
  return path.replace(/\\/g, '/').replace(/\/+/g, '/').toLowerCase();
}
